import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { reservationsApi, CalendarLinks } from '../services/api';
import { Reservation, CreateReservationDto } from '../types';

interface ReservationDetailModalProps {
  reservation: Reservation;
  onClose: () => void;
  onUpdate: () => void;
  onDelete: (id: string) => void;
}

const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
  reservation,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [calendarLinks, setCalendarLinks] = useState<CalendarLinks | null>(null);
  const [editForm, setEditForm] = useState<CreateReservationDto>({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    isAllDay: false,
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const canEdit = user?.role === 'admin' || user?.id === reservation.userId;

  useEffect(() => {
    // Load calendar links
    loadCalendarLinks();
  }, [reservation.id]);

  const loadCalendarLinks = async () => {
    try {
      const links = await reservationsApi.getCalendarLinks(reservation.id);
      setCalendarLinks(links);
    } catch (error) {
      console.error('Failed to load calendar links:', error);
    }
  };

  const startEditing = () => {
    const start = new Date(reservation.startTime);
    const end = new Date(reservation.endTime);

    if (reservation.isAllDay) {
      // For all-day events, extract UTC date and create local Date representing that calendar day
      // This prevents timezone conversion from shifting the displayed date
      setStartDate(new Date(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 12, 0, 0));
      setEndDate(new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 12, 0, 0));
    } else {
      setStartDate(start);
      setEndDate(end);
    }

    setEditForm({
      title: reservation.title,
      description: reservation.description || '',
      startTime: reservation.isAllDay ? '' : start.toTimeString().slice(0, 5),
      endTime: reservation.isAllDay ? '' : end.toTimeString().slice(0, 5),
      isAllDay: reservation.isAllDay || false,
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      isAllDay: false,
    });
  };

  const saveChanges = async () => {
    if (!editForm.title) {
      alert('Please fill in the title field');
      return;
    }

    if (!editForm.isAllDay && (!editForm.startTime || !editForm.endTime)) {
      alert('Please fill in start time and end time for timed events');
      return;
    }

    try {
      let startDateTime: Date;
      let endDateTime: Date;

      if (editForm.isAllDay) {
        const startYear = startDate!.getFullYear();
        const startMonth = startDate!.getMonth();
        const startDay = startDate!.getDate();
        startDateTime = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));

        const effectiveEndDate = endDate || startDate!;
        const endYear = effectiveEndDate.getFullYear();
        const endMonth = effectiveEndDate.getMonth();
        const endDay = effectiveEndDate.getDate();
        endDateTime = new Date(Date.UTC(endYear, endMonth, endDay, 23, 59, 59, 999));
      } else {
        startDateTime = new Date(startDate!);
        const [startHour, startMinute] = editForm.startTime.split(':');
        startDateTime.setHours(parseInt(startHour), parseInt(startMinute));

        const effectiveEndDate = endDate || startDate!;
        endDateTime = new Date(effectiveEndDate);
        const [endHour, endMinute] = editForm.endTime.split(':');
        endDateTime.setHours(parseInt(endHour), parseInt(endMinute));
      }

      await reservationsApi.update(reservation.id, {
        title: editForm.title,
        description: editForm.description,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        isAllDay: editForm.isAllDay,
      });

      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to update reservation:', error);
      if (error.response) {
        alert(`Failed to update reservation: ${error.response.data.message || 'Unknown error'}`);
      } else {
        alert('Failed to update reservation: Network error');
      }
    }
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this reservation?')) {
      onDelete(reservation.id);
    }
  };

  const formatDateTime = (dateString: string, isAllDay?: boolean) => {
    const date = new Date(dateString);
    if (isAllDay) {
      // For all-day events, use UTC values to avoid timezone shifting the date
      const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12, 0, 0));
      return utcDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDateForInput = (date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {!isEditing ? (
          // View Mode
          <>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-2xl font-bold text-slate-800 pr-4">{reservation.title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Owner Info */}
            <div className="mb-4 flex items-center text-gray-600">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>
                {reservation.user?.firstName} {reservation.user?.lastName}
                {user?.id === reservation.userId && <span className="text-blue-600 ml-1">(You)</span>}
              </span>
            </div>

            {/* Time Info */}
            <div className="mb-4 space-y-2">
              <div className="flex items-start text-gray-600">
                <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  {reservation.isAllDay ? (
                    <>
                      <div>{formatDateTime(reservation.startTime, true)}</div>
                      {new Date(reservation.startTime).toDateString() !== new Date(reservation.endTime).toDateString() && (
                        <div className="text-sm text-gray-500">
                          to {formatDateTime(reservation.endTime, true)}
                        </div>
                      )}
                      <div className="text-sm text-indigo-600 font-medium">All Day Event</div>
                    </>
                  ) : (
                    <>
                      <div>{formatDateTime(reservation.startTime)}</div>
                      <div className="text-sm text-gray-500">
                        to {formatDateTime(reservation.endTime)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {reservation.description && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                <p className="text-gray-600 whitespace-pre-wrap">{reservation.description}</p>
              </div>
            )}

            {/* Calendar Links */}
            {calendarLinks && (
              <div className="mb-6 border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Add to Calendar</h4>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={calendarLinks.googleCalendarUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <span className="mr-1">Google</span>
                  </a>
                  <a
                    href={calendarLinks.outlookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <span className="mr-1">Outlook</span>
                  </a>
                  <a
                    href={calendarLinks.icsUrl}
                    download
                    className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <span className="mr-1">Download .ics</span>
                  </a>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              {canEdit ? (
                <>
                  <button
                    onClick={startEditing}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Delete
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </>
              ) : (
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              )}
            </div>
          </>
        ) : (
          // Edit Mode
          <>
            <h3 className="text-2xl font-bold text-slate-800 mb-6">Edit Reservation</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Reservation title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional description"
                  rows={3}
                />
              </div>

              {/* All Day Checkbox */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="editAllDay"
                  checked={editForm.isAllDay}
                  onChange={(e) => {
                    setEditForm({
                      ...editForm,
                      isAllDay: e.target.checked,
                      startTime: e.target.checked ? '' : editForm.startTime,
                      endTime: e.target.checked ? '' : editForm.endTime,
                    });
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="editAllDay" className="text-sm font-medium text-gray-700">
                  All Day Event
                </label>
              </div>

              {/* Date/Time inputs */}
              {editForm.isAllDay ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                    <input
                      type="date"
                      value={startDate ? formatDateForInput(startDate) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const [year, month, day] = e.target.value.split('-').map(Number);
                          setStartDate(new Date(year, month - 1, day, 12, 0, 0));
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={endDate ? formatDateForInput(endDate) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const [year, month, day] = e.target.value.split('-').map(Number);
                          setEndDate(new Date(year, month - 1, day, 12, 0, 0));
                        } else {
                          setEndDate(null);
                        }
                      }}
                      min={startDate ? formatDateForInput(startDate) : ''}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                      <input
                        type="date"
                        value={startDate ? formatDateForInput(startDate) : ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            const [year, month, day] = e.target.value.split('-').map(Number);
                            setStartDate(new Date(year, month - 1, day));
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                      <input
                        type="date"
                        value={endDate ? formatDateForInput(endDate) : (startDate ? formatDateForInput(startDate) : '')}
                        onChange={(e) => {
                          if (e.target.value) {
                            const [year, month, day] = e.target.value.split('-').map(Number);
                            setEndDate(new Date(year, month - 1, day));
                          }
                        }}
                        min={startDate ? formatDateForInput(startDate) : ''}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                      <input
                        type="time"
                        value={editForm.startTime}
                        onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                      <input
                        type="time"
                        value={editForm.endTime}
                        onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={cancelEditing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveChanges}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReservationDetailModal;
